import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import Swal from 'sweetalert2';
import Web3 from 'web3';
import { ethers } from 'ethers';
import erc20abi from '../../helpers/abi';
import wallet from '../../helpers/wallet';
import './MercurXMintNftModal.scss';
import { abiRequestAction } from '../../store/abi/abiActions';
import { getBNB } from '../../store/token/tokenActions';
import { transactionRequest } from '../../store/transaction/transactionActions';
import { setLoadingAction } from '../../store/loading/loadingActions';
import * as loadingActionTypes from '../../store/loading/loadingActionTypes';
import {
  checkUserTokenRequestAction,
  accountDetailsRequestAction,
} from '../../store/account/userActions';
import {
  getMercurxProjectRequestAction,
  getProjectByID,
} from '../../store/project/projectActions';
import {
  buildMintFunctionArgs,
  getNftDisplayContractAddress,
  isAddress,
  resolveNftSaleConfig,
  toBaseUnits,
} from '../../helpers/nftSale';
import { mainColors } from '../../helpers/colors';
import MercurXModalLoadingOverlay from '../MercurXModalLoadingOverlay/MercurXModalLoadingOverlay';

const MINT_GAS_LIMIT_CAP = 16000000n;
const MIN_TRANSACTION_GAS_LIMIT = 21000n;

const toBigIntSafe = (value, fallback = 0n) => {
  try {
    return window.BigInt(value);
  } catch (error) {
    return fallback;
  }
};

const readContractMethod = async (contract, methodName, args = []) => {
  const method = contract?.methods?.[methodName];

  if (typeof method !== 'function') {
    return null;
  }

  try {
    return await method(...args).call();
  } catch (error) {
    return null;
  }
};

const resolveMintGasLimit = async (mintMethodCall, txOptions, web3) => {
  const [estimatedGas, latestBlock] = await Promise.all([
    mintMethodCall.estimateGas(txOptions),
    web3.eth.getBlock('latest'),
  ]);
  const estimatedGasValue = toBigIntSafe(estimatedGas);

  if (estimatedGasValue <= 0n) {
    throw new Error('Gas estimate could not be calculated for the NFT mint transaction.');
  }

  const blockGasLimitValue = toBigIntSafe(latestBlock?.gasLimit, MINT_GAS_LIMIT_CAP);
  const effectiveGasCap = blockGasLimitValue > 0n && blockGasLimitValue < MINT_GAS_LIMIT_CAP
    ? blockGasLimitValue
    : MINT_GAS_LIMIT_CAP;
  const bufferedGasLimit = (estimatedGasValue * 12n) / 10n;
  const gasLimit = bufferedGasLimit > effectiveGasCap ? effectiveGasCap : bufferedGasLimit;

  return (gasLimit > MIN_TRANSACTION_GAS_LIMIT ? gasLimit : MIN_TRANSACTION_GAS_LIMIT).toString();
};

const getConfigurationError = (mintConfig) => {
  if (!mintConfig.saleContractAddress) {
    return 'NFT sale contract address is missing in the backend project data.';
  }

  if (!mintConfig.contractAbi) {
    return 'NFT ABI could not be found in the /contracts response.';
  }

  if (!mintConfig.mintMethod) {
    return 'A supported mint method could not be found in the NFT ABI.';
  }

  return '';
};

const formatAmount = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '0';
  }

  return numericValue.toLocaleString('en-US', {
    maximumFractionDigits: 6,
  });
};

const truncateAddress = (value) => {
  if (!isAddress(value)) {
    return value || '-';
  }

  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const getBscScanAddressUrl = (address) => {
  return `https://testnet.bscscan.com/address/${address}`;
};

function MercurXMintNftModal({ ...props }) {
  const {
    show,
    onHide,
    project,
    abiHistory,
    abiHistoryRequest,
    getBNBRequest,
    bnbData,
    mercurxProject,
    getMercurxProjectRequest,
    isLoading,
    setLoading,
    transactionRequest,
    checkUserTokenWeb3,
    getProjectByIDRequest,
    accountDetailsRequest,
  } = props;
  const quantity = '1';
  const [imageError, setImageError] = useState(false);

  const isMintLoading = Boolean(isLoading?.[loadingActionTypes.MINT_NFT_LOADING]);
  const mintConfig = resolveNftSaleConfig(project, abiHistory, mercurxProject, bnbData);
  const configurationError = getConfigurationError(mintConfig);
  const parsedQuantity = parseInt(quantity, 10);
  const quantityValue = Number.isInteger(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
  const quantityError =
    quantity.trim() === '' ||
    !Number.isInteger(parsedQuantity) ||
    parsedQuantity < 1;
  const totalTokenValue = mintConfig.unitPriceInToken * quantityValue;
  const totalNativeValue = mintConfig.unitPriceInNativeCurrency * quantityValue;
  const displayContractAddress = mintConfig.saleContractAddress ||
    getNftDisplayContractAddress(project);
  const nftName = project?.nft?.name || project?.name || 'NFT';
  const nftSymbol = (project?.nft?.symbol || project?.name || 'NFT').slice(0, 8).toUpperCase();
  const projectImageUrl =
    project?.id && process.env.REACT_APP_API_URL
      ? `${process.env.REACT_APP_API_URL}/projects/${project.id}/image`
      : '';
  const paymentRouteLabel = mintConfig.paymentType === 'native'
    ? 'Direct BNB checkout'
    : `${mintConfig.paymentTokenSymbol} approval + mint`;
  const unitPriceLabel = mintConfig.paymentType === 'native'
    ? `${formatAmount(mintConfig.unitPriceInNativeCurrency)} BNB`
    : `${formatAmount(mintConfig.unitPriceInToken)} ${mintConfig.paymentTokenSymbol}`;
  const totalPaymentLabel = mintConfig.paymentType === 'native'
    ? `${formatAmount(totalNativeValue)} BNB`
    : `${formatAmount(totalTokenValue)} ${mintConfig.paymentTokenSymbol}`;

  useEffect(() => {
    if (!show) {
      return;
    }

    setImageError(false);
    abiHistoryRequest();
    getMercurxProjectRequest();
    getBNBRequest();
  }, [show, abiHistoryRequest, getMercurxProjectRequest, getBNBRequest]);

  const closeModal = () => {
    if (isMintLoading) {
      return;
    }

    onHide();
  };

  const renderAddressLink = (address, tooltipId) => {
    const label = truncateAddress(address);

    if (!isAddress(address)) {
      return label;
    }

    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={tooltipId}>{address}</Tooltip>}
      >
        <a
          href={getBscScanAddressUrl(address)}
          className="nft-mint-address-link"
          target="_blank"
          rel="noreferrer"
        >
          {label}
        </a>
      </OverlayTrigger>
    );
  };

  const refreshProjectData = () => {
    if (project?.id) {
      getProjectByIDRequest({ id: project.id });
    }

    accountDetailsRequest();
  };

  const handleMint = async (event) => {
    event.preventDefault();

    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    if (!userData?.token) {
      Swal.fire({
        icon: 'warning',
        iconColor: '#E40039',
        confirmButtonColor: '#E40039',
        text: 'Please sign in before starting the NFT mint transaction.',
      });
      return;
    }

    if (quantityError) {
      return;
    }

    checkUserTokenWeb3();
    setLoading({ key: loadingActionTypes.MINT_NFT_LOADING, isLoading: true });

    let signerAddress = null;

    try {
      if (typeof window.ethereum === 'undefined') {
        throw new Error('MetaMask or another injected wallet is required for NFT minting.');
      }

      if (configurationError) {
        throw new Error(configurationError);
      }

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await wallet.controlAndSwitchOrAddNetwork();
      await provider.send('eth_requestAccounts', []);

      const signer = provider.getSigner();
      signerAddress = await signer.getAddress();
      const web3 = new Web3(window.ethereum);

      if (
        mintConfig.paymentType === 'native' &&
        mintConfig.unitPriceInNativeCurrency <= 0 &&
        Number(project?.nft?.price_in_usd) > 0
      ) {
        throw new Error(
          'NFT native payment amount could not be calculated from the current backend data.',
        );
      }

      if (
        mintConfig.paymentType === 'erc20' &&
        mintConfig.unitPriceInToken <= 0 &&
        Number(project?.nft?.price_in_mercurx) > 0
      ) {
        throw new Error(
          'NFT token payment amount could not be calculated from the current backend data.',
        );
      }

      const saleContract = new web3.eth.Contract(
        mintConfig.contractAbi,
        mintConfig.saleContractAddress,
      );
      const onChainSaleActive = await readContractMethod(saleContract, 'saleActive');

      if (onChainSaleActive === false) {
        throw new Error('This NFT sale is not active right now.');
      }

      const [
        onChainMaxPerWallet,
        onChainPurchasedCount,
        onChainCollectionAddress,
      ] = await Promise.all([
        readContractMethod(saleContract, 'maxPerWallet'),
        readContractMethod(saleContract, 'purchasedCount', [signerAddress]),
        readContractMethod(saleContract, 'ticketNFT'),
      ]);
      const walletLimit = toBigIntSafe(onChainMaxPerWallet);
      const purchasedCount = toBigIntSafe(onChainPurchasedCount);
      const requestedQuantity = window.BigInt(quantityValue);

      if (walletLimit > 0n && purchasedCount + requestedQuantity > walletLimit) {
        throw new Error(
          `Wallet limit exceeded. This wallet already minted ${purchasedCount.toString()} ` +
          `of ${walletLimit.toString()} allowed ticket${walletLimit === 1n ? '' : 's'}.`,
        );
      }

      const resolvedCollectionAddress = isAddress(onChainCollectionAddress)
        ? onChainCollectionAddress
        : mintConfig.collectionContractAddress;

      let approvalHash = null;

      if (mintConfig.paymentType === 'erc20') {
        const [onChainPaymentTokenAddress, onChainPricePerTicket] = await Promise.all([
          readContractMethod(saleContract, 'paymentToken'),
          readContractMethod(saleContract, 'pricePerTicket'),
        ]);
        const effectivePaymentTokenAddress = isAddress(onChainPaymentTokenAddress)
          ? onChainPaymentTokenAddress
          : mintConfig.paymentTokenAddress;
        const totalTokenAmount = onChainPricePerTicket !== null
          ? (toBigIntSafe(onChainPricePerTicket) * requestedQuantity).toString()
          : toBaseUnits(String(totalTokenValue), mintConfig.paymentTokenDecimals);

        if (toBigIntSafe(totalTokenAmount) > 0n) {
          if (!isAddress(effectivePaymentTokenAddress)) {
            throw new Error(
              'NFT payment token address is missing in the sale contract and backend project data.',
            );
          }

          const paymentTokenContract = new web3.eth.Contract(
            erc20abi,
            effectivePaymentTokenAddress,
          );
          const allowance = await paymentTokenContract.methods
            .allowance(signerAddress, mintConfig.saleContractAddress)
            .call();

          if (
            ethers.BigNumber.from(allowance || '0').lt(
              ethers.BigNumber.from(totalTokenAmount),
            )
          ) {
            const approvalTx = await paymentTokenContract.methods
              .approve(mintConfig.saleContractAddress, totalTokenAmount)
              .send({
                from: signerAddress,
                to: effectivePaymentTokenAddress,
              });

            approvalHash = approvalTx?.transactionHash || null;
          }
        }
      }

      const mintArgs = buildMintFunctionArgs(mintConfig.mintMethod, {
        quantity: quantityValue,
        signerAddress,
      });
      const txOptions = {
        from: signerAddress,
        to: mintConfig.saleContractAddress,
      };

      if (mintConfig.paymentType === 'native') {
        const nativeValueInWei = toBaseUnits(String(totalNativeValue), 18);
        if (ethers.BigNumber.from(nativeValueInWei).gt(0)) {
          txOptions.value = nativeValueInWei;
        }
      }

      const mintMethodCall = saleContract.methods[mintConfig.mintMethod.name](...mintArgs);
      txOptions.gas = await resolveMintGasLimit(mintMethodCall, txOptions, web3);

      const transaction = await mintMethodCall.send(txOptions);
      const transactionPayload = {
        project_id: project?.id,
        project_name: project?.name,
        token_count: quantityValue,
        user_public_address: signerAddress,
        token_address: resolvedCollectionAddress || mintConfig.saleContractAddress,
        transaction_hash: transaction?.transactionHash,
        transaction_time: new Date().toISOString(),
        transaction_status: true,
        action: 'mint',
      };

      transactionRequest(transactionPayload);
      refreshProjectData();

      const approvalLink = approvalHash
        ? `<br /><a href="https://testnet.bscscan.com/tx/${approvalHash}" ` +
          `target="_blank" rel="noreferrer">Check approval transaction</a>`
        : '';

      await Swal.fire({
        icon: 'success',
        iconColor: mainColors.primary,
        confirmButtonColor: mainColors.primary,
        title: 'Mint transaction succeeded',
        html:
          `<a href="https://testnet.bscscan.com/tx/${transaction?.transactionHash}" ` +
          `target="_blank" rel="noreferrer">Check mint transaction</a>${approvalLink}`,
      });

      onHide();
    } catch (error) {
      const failedTransactionHash = error?.receipt?.transactionHash || null;

      transactionRequest({
        project_id: project?.id,
        project_name: project?.name,
        token_count: quantityValue,
        user_public_address: signerAddress,
        token_address: mintConfig.collectionContractAddress || mintConfig.saleContractAddress,
        transaction_hash: failedTransactionHash,
        transaction_time: new Date().toISOString(),
        transaction_status: false,
        action: 'mint',
      });

      refreshProjectData();

      await Swal.fire({
        icon: failedTransactionHash ? 'error' : 'warning',
        iconColor: '#E40039',
        confirmButtonColor: '#E40039',
        title: error?.receipt?.transactionHash
          ? 'Mint transaction failed'
          : 'Mint could not start',
        html: error?.receipt?.transactionHash
          ? `<a href="https://testnet.bscscan.com/tx/${error.receipt.transactionHash}" ` +
            `target="_blank" rel="noreferrer">Check failed transaction</a>`
          : error?.message,
      });
    } finally {
      setLoading({ key: loadingActionTypes.MINT_NFT_LOADING, isLoading: false });
    }
  };

  return (
    <Modal
      show={show}
      onHide={closeModal}
      centered
      backdrop={isMintLoading ? 'static' : true}
      keyboard={!isMintLoading}
      dialogClassName={`nft-mint-modal mercurx-modal-loading-shell${
        isMintLoading ? ' mercurx-modal-loading-shell--loading' : ''
      }`}
    >
      <Modal.Header closeButton={!isMintLoading} className="nft-mint-modal-header border-bottom-0">
        <div>
          <div className="nft-mint-eyebrow">NFT Mint</div>
          <Modal.Title className="nft-mint-modal-title">Mint Access</Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body className="nft-mint-modal-body">
        <form className="nft-mint-form" onSubmit={handleMint}>
          <div className="nft-mint-hero">
            <div className="nft-mint-hero-media">
              {projectImageUrl && !imageError ? (
                <img
                  className="nft-mint-hero-image"
                  src={projectImageUrl}
                  alt={nftName}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="nft-mint-hero-fallback">{nftSymbol.slice(0, 4)}</div>
              )}
            </div>
            <div className="nft-mint-hero-copy">
              <div className="nft-mint-chip-row">
                <span className="nft-mint-chip nft-mint-chip--accent">NFT Launch</span>
                <span className="nft-mint-chip">Wallet Mint</span>
              </div>
              <h3 className="nft-mint-title">{nftName}</h3>
              <p className="nft-mint-subtitle">
                Confirm the wallet transaction, keep the backend log in sync, and route minting
                through the configured sale contract.
              </p>
              <div className="nft-mint-hero-meta">
                <div className="nft-mint-hero-meta-item">
                  <span className="nft-mint-hero-label">Collection</span>
                  <span className="nft-mint-hero-value">{nftSymbol}</span>
                </div>
                <div className="nft-mint-hero-meta-item">
                  <span className="nft-mint-hero-label">Payment flow</span>
                  <span className="nft-mint-hero-value">{paymentRouteLabel}</span>
                </div>
                <div className="nft-mint-hero-meta-item">
                  <span className="nft-mint-hero-label">Sale contract</span>
                  <span className="nft-mint-hero-value">
                    {renderAddressLink(displayContractAddress, 'nft-sale-contract-tooltip')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {configurationError && (
            <div className="nft-mint-error-box">
              <strong>Configuration issue:</strong> {configurationError}
            </div>
          )}

          <div className="nft-mint-panels">
            <div className="nft-mint-panel">
              <div className="nft-mint-panel-title">Transaction setup</div>
              <div className="nft-mint-summary-row">
                <span>Mint method</span>
                <span>{mintConfig.mintMethodName || '-'}</span>
              </div>
              <div className="nft-mint-summary-row">
                <span>Unit price</span>
                <span>{unitPriceLabel}</span>
              </div>
              <div className="nft-mint-summary-row">
                <span>Payment asset</span>
                <span>
                  {mintConfig.paymentType === 'native'
                    ? 'BNB native transfer'
                    : mintConfig.paymentTokenSymbol}
                </span>
              </div>
              <div className="nft-mint-summary-row">
                <span>Collection contract</span>
                <span>
                  {renderAddressLink(
                    mintConfig.collectionContractAddress,
                    'nft-collection-contract-tooltip',
                  )}
                </span>
              </div>
              {mintConfig.contractAbiKey && (
                <div className="nft-mint-summary-row">
                  <span>ABI source</span>
                  <span>{mintConfig.contractAbiKey}</span>
                </div>
              )}
            </div>

            <div className="nft-mint-panel">
              <div className="nft-mint-panel-title">Mint quantity</div>
              <Form.Label className="nft-mint-label" htmlFor="nftQuantity">
                Number of NFTs
              </Form.Label>
              <Form.Control
                id="nftQuantity"
                className="nft-mint-input"
                type="number"
                min="1"
                step="1"
                value={quantity}
                disabled
                readOnly
              />
              {quantityError && (
                <Form.Text className="text-danger d-block mt-2">
                  Quantity must be an integer greater than 0.
                </Form.Text>
              )}

              <div className="nft-mint-total-card">
                <span className="nft-mint-total-label">Total to confirm</span>
                <span className="nft-mint-total-value">{totalPaymentLabel}</span>
              </div>

              <div className="nft-mint-note">
                The mint transaction is recorded in backend transaction history after wallet
                confirmation.
              </div>

              {mintConfig.paymentType === 'erc20' && (
                <div className="nft-mint-note">
                  If allowance is missing, MetaMask will request an approval transaction before
                  the mint step.
                </div>
              )}
            </div>
          </div>

          <div className="nft-mint-actions">
            <Button
              type="button"
              variant="custom"
              className="nft-mint-action nft-mint-action--secondary"
              onClick={closeModal}
              disabled={isMintLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="custom"
              className="nft-mint-action nft-mint-action--primary"
              disabled={isMintLoading || quantityError || Boolean(configurationError)}
            >
              {!isMintLoading ? (
                'Mint Now'
              ) : (
                <span className="d-flex align-items-center justify-content-center">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Waiting for confirmation...</span>
                </span>
              )}
            </Button>
          </div>
        </form>
      </Modal.Body>

      <MercurXModalLoadingOverlay
        show={isMintLoading}
        eyebrow="Transaction In Progress"
        title="Waiting for wallet confirmation"
        description={
          'The mint request is being prepared and sent. Please keep this window open while ' +
          'MetaMask and the network finish the transaction.'
        }
        note="The modal is temporarily locked to prevent accidental close or double-submit."
      />
    </Modal>
  );
}

const mapStateToProps = (state) => {
  return {
    project: state.projectReducer.project,
    mercurxProject: state.projectReducer.mercurxProject,
    abiHistory: state.abiReducer.abiHistory,
    bnbData: state.tokenReducer.bnb,
    isLoading: state.loadingReducer.isLoading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    abiHistoryRequest: (payload) => {
      dispatch(abiRequestAction(payload));
    },
    getBNBRequest: (payload) => {
      dispatch(getBNB(payload));
    },
    getMercurxProjectRequest: (payload) => {
      dispatch(getMercurxProjectRequestAction(payload));
    },
    setLoading: (payload) => {
      dispatch(setLoadingAction(payload));
    },
    transactionRequest: (payload) => {
      dispatch(transactionRequest(payload));
    },
    checkUserTokenWeb3: (payload) => {
      dispatch(checkUserTokenRequestAction(payload));
    },
    getProjectByIDRequest: (payload) => {
      dispatch(getProjectByID(payload));
    },
    accountDetailsRequest: (payload) => {
      dispatch(accountDetailsRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MercurXMintNftModal);
